import { useCallback, useMemo, useState } from "react";

function buildMockViewModel() {
  return {
    cards: [
      {
        title: "Usuários",
        values: {
          Total: 12840,
          Ativos: 11402,
          Cancelados: 1438,
        },
      },
      {
        title: "Receita",
        values: {
          "MRR (R$)": 284390,
          "Pagos": 8270,
          "Free": 4570,
        },
      },
      {
        title: "Conversão",
        values: {
          "Trial → Pago": "12.4%",
          Churn: "2.1%",
        },
      },
    ],
    pie: {
      title: "Distribuição por Plano",
      data: [
        { type: "Mensal", value: 5400 },
        { type: "Anual", value: 2200 },
        { type: "Free", value: 4570 },
        { type: "Legado", value: 670 },
      ],
    },
    table: {
      title: "Top Planos",
      rows: [
        {
          key: "1",
          product: "Afinando Plus",
          plan: "Mensal",
          type: "Base",
          tag: "Recorrente",
          amt: 5400,
          amt_sub: 1,
        },
        {
          key: "2",
          product: "Afinando o Cérebro",
          plan: "Anual",
          type: "Base",
          tag: "Recorrente",
          amt: 2200,
          amt_sub: 1,
        },
        {
          key: "3",
          product: "Afinando o Cérebro",
          plan: "Free",
          type: "Base",
          tag: "Único",
          amt: 4570,
          amt_sub: 1,
        },
      ],
    },
    raw: null,
  };
}

function toBooleanEnv(value, defaultValue) {
  if (value === undefined || value === null || value === "") return defaultValue;
  return value === "1" || value === "true" || value === "yes";
}

export default function useDashboardProtoData(options = {}) {
  const mode = useMemo(() => {
    const rawMode = options.mode || process.env.REACT_APP_DASHBOARD_PROTO_MODE;
    return rawMode === "api" ? "api" : "mock";
  }, [options.mode]);

  const endpoint = useMemo(() => {
    if (options.endpoint) return options.endpoint;
    const fromEnv = process.env.REACT_APP_DASHBOARD_PROTO_URL;
    if (fromEnv) return fromEnv;
    return "https://swdhuvwq3sabyelhlf7zx5egxq0wscnw.lambda-url.us-east-1.on.aws/";
  }, [options.endpoint]);

  const endpointNormalized = useMemo(() => {
    const endpointTrim = String(endpoint || "").trim();
    if (!endpointTrim) return "";
    return /^https?:\/\//i.test(endpointTrim) ? endpointTrim : `https://${endpointTrim}`;
  }, [endpoint]);

  const noAuth = useMemo(() => {
    if (options.noAuth !== undefined) return options.noAuth;
    return toBooleanEnv(process.env.REACT_APP_DASHBOARD_PROTO_NOAUTH, false);
  }, [options.noAuth]);

  const requestHeader = useMemo(() => {
    return options.header || null;
  }, [options.header]);

  const [viewModel, setViewModel] = useState(() => buildMockViewModel());
  const [raw, setRaw] = useState(null);
  const [loadingMock, setLoadingMock] = useState(false);
  const [errorMock, setErrorMock] = useState("");
  const [loadingApi, setLoadingApi] = useState(false);
  const [errorApiLocal, setErrorApiLocal] = useState("");
  const [lastRequestUrl, setLastRequestUrl] = useState("");
  const [debugInfo, setDebugInfo] = useState("");

  const load = useCallback(
    async ({ dateStart, dateEnd, filterType, filterValue } = {}) => {
      if (mode === "mock") {
        setErrorMock("");
        setLoadingMock(true);
        window.setTimeout(() => {
          const next = buildMockViewModel();
          next.raw = { dateStart, dateEnd, filterType, filterValue };
          setRaw(next.raw);
          setViewModel(next);
          setLoadingMock(false);
        }, 250);
        return;
      }

      setErrorApiLocal("");
      if (!endpointNormalized) {
        setErrorApiLocal("Defina REACT_APP_DASHBOARD_PROTO_URL para usar o modo api.");
        return;
      }

      if (
        typeof window !== "undefined" &&
        window.location &&
        window.location.protocol === "https:" &&
        endpointNormalized.startsWith("http://")
      ) {
        setErrorApiLocal("Sua aplicação está em HTTPS. Use uma URL HTTPS para a API (Lambda Function URL é sempre https://).");
        return;
      }

      const query = new URLSearchParams();
      if (dateStart) query.set("date_start", dateStart);
      if (dateEnd) query.set("date_end", dateEnd);
      if (filterType) query.set("filter_type", filterType);
      if (filterValue) query.set("filter_value", filterValue);
      const url = query.toString() ? `${endpointNormalized}?${query.toString()}` : endpointNormalized;
      setLastRequestUrl(url);
      setDebugInfo("");

      setLoadingApi(true);
      try {
        const origin =
          typeof window !== "undefined" && window.location ? window.location.origin : "";

        let noCorsOk = false;
        let noCorsError = "";
        try {
          await fetch(url, { method: "GET", mode: "no-cors", credentials: "omit" });
          noCorsOk = true;
        } catch (e) {
          noCorsError = e && e.message ? String(e.message) : String(e);
        }

        const headers =
          noAuth || !requestHeader
            ? undefined
            : Object.keys(requestHeader).length > 0
              ? requestHeader
              : undefined;

        const res = await fetch(url, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          headers,
        });

        const rawText = await res.text();
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${rawText}`);
        }

        let apiResponse;
        try {
          apiResponse = rawText ? JSON.parse(rawText) : null;
        } catch (e) {
          apiResponse = rawText;
        }

        setDebugInfo(
          JSON.stringify(
            {
              origin,
              url,
              noCorsOk,
              noCorsError: noCorsError || undefined,
              status: res.status,
            },
            null,
            2,
          ),
        );

        setErrorApiLocal("");
        if (apiResponse && apiResponse.cards && apiResponse.table && apiResponse.pie) {
          setRaw(apiResponse.raw || apiResponse);
          setViewModel({ ...apiResponse, raw: apiResponse.raw || apiResponse });
          return;
        }

        setRaw(apiResponse);
        setViewModel((prev) => ({ ...prev, raw: apiResponse }));
      } catch (err) {
        const origin =
          typeof window !== "undefined" && window.location ? window.location.origin : "";

        let noCorsOk = false;
        let noCorsError = "";
        try {
          await fetch(url, { method: "GET", mode: "no-cors", credentials: "omit" });
          noCorsOk = true;
        } catch (e) {
          noCorsError = e && e.message ? String(e.message) : String(e);
        }

        const message = err && err.message ? String(err.message) : String(err);
        setDebugInfo(
          JSON.stringify(
            {
              origin,
              url,
              noCorsOk,
              noCorsError: noCorsError || undefined,
              error: message,
            },
            null,
            2,
          ),
        );
        setErrorApiLocal(`Falha ao chamar a API: ${message}`);
      } finally {
        setLoadingApi(false);
      }
    },
    [endpointNormalized, mode, noAuth, requestHeader],
  );

  const isLoading = mode === "api" ? loadingApi : loadingMock;
  const error = mode === "api" ? errorApiLocal : errorMock;

  return {
    mode,
    viewModel,
    raw,
    isLoading,
    error,
    endpoint: endpointNormalized,
    lastRequestUrl,
    debugInfo,
    load,
  };
}
